import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { AppLayout } from '@/components/AppLayout';
import { ArrowLeft, Copy, Download, FileText, RefreshCw, Send } from 'lucide-react';
import { PaginatedTemplate1 } from '@/components/ResumeTemplates/PaginatedTemplate1';
import { ResumeMatchAnalysisEmerald } from '@/components/ResumeMatchAnalysisEmerald';
import ResumeMatchAnalysisModal from '@/components/ResumeMatchAnalysisModal';
import { LetterTemplate1 } from '@/components/LetterTemplates/LetterTemplate1';
import { BioTemplate1 } from '@/components/LetterTemplates/BioTemplate1';
import { 
  LetterData, 
  ExecutiveBioData, 
  VectorShiftLetterData,
  convertVectorShiftToLetterData, 
  parseLetterContent, 
  parseExecutiveBioContent 
} from '@/components/LetterTemplates/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

interface TailoredDocument {
  id: string;
  type: 'resume' | 'cover-letter' | 'thank-you-letter' | 'executive-bio' | 'linkedin-profile' | 'outreach-letter';
  title: string;
  content: string;
  resumeJson?: any;
  letterData?: LetterData;
  bioData?: ExecutiveBioData;
}

const isResumeJsonLike = (value: any): boolean => {
  if (!value || typeof value !== 'object') return false;
  return Boolean(value.Name || value.Sections || value.Contact || value.Summary || value.Experience);
};

const isVectorShiftLetterJson = (value: any): value is VectorShiftLetterData => {
  return value && typeof value === 'object' && typeof value.Body === 'string' && typeof value.Name === 'string';
};

const extractLetterBody = (value: any): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && typeof value.Body === 'string') return value.Body;
  return '';
};

const ResumeResults: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const tailoringResults = location.state?.tailoringResults || null;
  const selectedDocuments: string[] = location.state?.selectedDocuments || ['resume'];

  console.log('=== RESUME RESULTS DEBUG ===');
  console.log('tailoringResults:', tailoringResults);

  if (!tailoringResults) {
    return (
      <AppLayout currentPage="resume-results">
        <div className="min-h-screen bg-background">
          <div className="container mx-auto py-8">
            <Card>
              <CardContent className="p-8 text-center">
                <h2 className="text-xl font-semibold mb-4">No Results Available</h2>
                <p className="text-muted-foreground mb-6">
                  No tailoring results found. Please run the tailoring process first.
                </p>
                <Button onClick={() => navigate('/resume-tailoring-fixed-duplicate')}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Resume Tailoring
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </AppLayout>
    );
  }

  // === RESUME JSON ===
  const resumeJson = useMemo(() => {
    if (tailoringResults?.Resume_JSON && typeof tailoringResults.Resume_JSON === 'object') {
      return tailoringResults.Resume_JSON;
    }
    if (isResumeJsonLike(tailoringResults?.Final_Resume_Text)) {
      return tailoringResults.Final_Resume_Text;
    }
    return null;
  }, [tailoringResults]);

  const resumeTextFallback = typeof tailoringResults?.Final_Resume_Text === 'string'
    ? tailoringResults.Final_Resume_Text
    : '';

  // === LETTER DATA (Cover Letter, Thank You, Outreach) ===
  const coverLetterRaw = tailoringResults?.Cover_Letter || tailoringResults?.Cover_Letter_JSON;
  const thankYouRaw = tailoringResults?.Thank_You_Letter || tailoringResults?.Thank_You_Letter_JSON;
  const outreachRaw = tailoringResults?.Outreach_Letter || tailoringResults?.Outreach_Email_JSON;
  const executiveBioRaw = tailoringResults?.Executive_Bio || tailoringResults?.Executive_Bio_JSON;
  const linkedinRaw = tailoringResults?.Linkedin_Profile;

  const companyName = tailoringResults?.company_name || '';
  const jobTitle = tailoringResults?.job_title || '';

  // Convert to LetterData for templates
  const coverLetterData: LetterData | undefined = useMemo(() => {
    if (!coverLetterRaw) return undefined;
    if (isVectorShiftLetterJson(coverLetterRaw)) {
      return convertVectorShiftToLetterData(coverLetterRaw, companyName, jobTitle);
    }
    if (typeof coverLetterRaw === 'string') {
      return parseLetterContent(coverLetterRaw);
    }
    return undefined;
  }, [coverLetterRaw, companyName, jobTitle]);

  const thankYouData: LetterData | undefined = useMemo(() => {
    if (!thankYouRaw) return undefined;
    if (isVectorShiftLetterJson(thankYouRaw)) {
      return convertVectorShiftToLetterData(thankYouRaw, companyName, jobTitle);
    }
    if (typeof thankYouRaw === 'string') {
      return parseLetterContent(thankYouRaw);
    }
    return undefined;
  }, [thankYouRaw, companyName, jobTitle]);

  const outreachData: LetterData | undefined = useMemo(() => {
    if (!outreachRaw) return undefined;
    if (isVectorShiftLetterJson(outreachRaw)) {
      return convertVectorShiftToLetterData(outreachRaw, companyName, jobTitle);
    }
    if (typeof outreachRaw === 'string') {
      return parseLetterContent(outreachRaw);
    }
    return undefined;
  }, [outreachRaw, companyName, jobTitle]);

  // === EXECUTIVE BIO DATA ===
  const executiveBioData: ExecutiveBioData | undefined = useMemo(() => {
    if (!executiveBioRaw) return undefined;
    if (typeof executiveBioRaw === 'object' && executiveBioRaw.name) {
      return executiveBioRaw as ExecutiveBioData;
    }
    const bodyText = extractLetterBody(executiveBioRaw);
    if (bodyText) {
      return parseExecutiveBioContent(bodyText);
    }
    return undefined;
  }, [executiveBioRaw]);

  // === ANALYSIS DATA ===
  const beforeScore = tailoringResults?.Original_Match_Score ?? null;
  const afterScore = tailoringResults?.Tailored_Match_Score ?? null;
  const improvement = (beforeScore !== null && afterScore !== null) ? afterScore - beforeScore : null;
  const analysisResults = tailoringResults?.changes_analysis || tailoringResults?.improvement_report || undefined;
  const changesAnalysis = tailoringResults?.changes_analysis || undefined;
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  
  // Tailor Again state
  const [showTailorAgainDialog, setShowTailorAgainDialog] = useState(false);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [isTailoringAgain, setIsTailoringAgain] = useState(false);

  const handleTailorAgain = () => {
    setShowTailorAgainDialog(true);
  };

  const handleSubmitTailorAgain = () => {
    setIsTailoringAgain(true);
    
    // Navigate back to tailoring page with special instructions and existing data
    navigate('/resume-tailoring-fixed-duplicate', {
      state: {
        retailorMode: true,
        specialInstructions: specialInstructions,
        previousResults: tailoringResults,
        resumeContent: resumeTextFallback,
        resumeJson: resumeJson,
        jobTitle: jobTitle,
        companyName: companyName,
      }
    });
    
    toast({
      title: "Tailoring again",
      description: "Redirecting with your special instructions...",
    });
    
    setShowTailorAgainDialog(false);
    setIsTailoringAgain(false);
  };

  // === BUILD DOCUMENTS ARRAY ===
  const documents: TailoredDocument[] = [
    {
      id: '1',
      type: 'resume',
      title: 'Tailored Resume',
      content: resumeTextFallback,
      resumeJson,
    },
    ...(selectedDocuments.includes('cover-letter') && coverLetterData ? [{
      id: '2',
      type: 'cover-letter' as const,
      title: 'Cover Letter',
      content: extractLetterBody(coverLetterRaw),
      letterData: coverLetterData,
    }] : []),
    ...(selectedDocuments.includes('thank-you-letter') && thankYouData ? [{
      id: '3',
      type: 'thank-you-letter' as const,
      title: 'Thank You Letter',
      content: extractLetterBody(thankYouRaw),
      letterData: thankYouData,
    }] : []),
    ...(selectedDocuments.includes('executive-bio') && executiveBioData ? [{
      id: '4',
      type: 'executive-bio' as const,
      title: 'Executive Bio',
      content: extractLetterBody(executiveBioRaw),
      bioData: executiveBioData,
    }] : []),
    ...(selectedDocuments.includes('linkedin-profile') && linkedinRaw ? [{
      id: '5',
      type: 'linkedin-profile' as const,
      title: 'LinkedIn Profile',
      content: extractLetterBody(linkedinRaw),
    }] : []),
    ...(selectedDocuments.includes('outreach-email') && outreachData ? [{
      id: '6',
      type: 'outreach-letter' as const,
      title: 'Outreach Email',
      content: extractLetterBody(outreachRaw),
      letterData: outreachData,
    }] : []),
  ];

  const [activeTab, setActiveTab] = useState(documents[0]?.id || '1');
  const currentDocument = documents.find((doc) => doc.id === activeTab);

  const handleCopy = async (content: string, label: string) => {
    try {
      await navigator.clipboard.writeText(content);
      toast({ title: `Copied ${label}` });
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' });
    }
  };

  const handleDownloadTxt = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Render document based on type
  const renderDocument = (doc: TailoredDocument) => {
    switch (doc.type) {
      case 'resume':
        return doc.resumeJson ? (
          <div className="bg-white">
            <PaginatedTemplate1
              data={doc.resumeJson}
              margins={{ top: 0.5, bottom: 0.5, left: 0.5, right: 0.5 }}
            />
          </div>
        ) : (
          <div className="bg-white p-8 whitespace-pre-wrap font-mono text-sm" style={{ width: '8.5in', minHeight: '11in' }}>
            {doc.content}
          </div>
        );

      case 'cover-letter':
      case 'thank-you-letter':
      case 'outreach-letter':
        return doc.letterData ? (
          <LetterTemplate1
            data={doc.letterData}
            margins={{ top: 0.5, bottom: 0.5, left: 0.5, right: 0.5 }}
          />
        ) : (
          <div className="bg-white p-8 whitespace-pre-wrap font-serif text-sm" style={{ width: '8.5in', minHeight: '11in' }}>
            {doc.content}
          </div>
        );

      case 'executive-bio':
        return doc.bioData ? (
          <BioTemplate1
            data={doc.bioData}
            margins={{ top: 0.5, bottom: 0.5, left: 0.5, right: 0.5 }}
          />
        ) : (
          <div className="bg-white p-8 whitespace-pre-wrap font-serif text-sm" style={{ width: '8.5in', minHeight: '11in' }}>
            {doc.content}
          </div>
        );

      case 'linkedin-profile':
        // LinkedIn doesn't have a letter template - just display nicely
        return (
          <div className="bg-white p-8 font-sans text-sm" style={{ width: '8.5in', minHeight: '11in' }}>
            <h1 className="text-2xl font-bold text-blue-700 mb-4 border-b-2 border-blue-700 pb-2">LinkedIn Profile</h1>
            <div className="whitespace-pre-wrap leading-relaxed">
              {doc.content}
            </div>
          </div>
        );

      default:
        return (
          <div className="bg-white p-8 whitespace-pre-wrap text-sm" style={{ width: '8.5in', minHeight: '11in' }}>
            {doc.content}
          </div>
        );
    }
  };

  return (
    <AppLayout currentPage="resume-results">
      <div className="min-h-screen bg-background">
        <PageHeader
          title="Tailored Documents"
          description="Your customized resume and documents"
          backTo="/resume-tailoring-fixed-duplicate"
        />

        <div className="container mx-auto py-8 space-y-6 max-w-4xl px-4">
          
          {/* Score Analysis Section */}
          {beforeScore !== null && afterScore !== null && improvement !== null && (
            <ResumeMatchAnalysisEmerald
              beforeScore={beforeScore}
              afterScore={afterScore}
              improvement={improvement}
              jobTitle={jobTitle || 'Position'}
              companyName={companyName || 'Company'}
              onShowAnalysisModal={() => setShowAnalysisModal(true)}
            />
          )}

          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl font-bold flex items-center gap-2">
                  <FileText className="w-6 h-6 text-primary" />
                  Tailored Documents
                </CardTitle>

                {currentDocument && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleTailorAgain}
                      className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Tailor this Resume Again
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCopy(currentDocument.content, currentDocument.title)}
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadTxt(currentDocument.content, `${currentDocument.title.replace(/\s+/g, '_')}.txt`)}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>

            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                <div className="bg-background border-b border-border flex items-end px-2 flex-shrink-0">
                  <div className="flex flex-wrap items-end -mb-px gap-1">
                    {documents.map((doc, index) => (
                      <button
                        key={doc.id}
                        className={`
                          relative flex items-center transition-all duration-200 rounded-t-lg min-w-0 px-3 py-2
                          ${activeTab === doc.id
                            ? 'font-semibold text-white bg-primary border-x border-t border-primary shadow-sm z-10'
                            : 'font-medium text-muted-foreground bg-muted/30 border border-b-0 border-primary/20 hover:bg-primary/10 hover:text-primary'
                          }
                        `}
                        onClick={() => setActiveTab(doc.id)}
                      >
                        <span
                          className={`
                            inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold mr-1.5
                            ${activeTab === doc.id ? 'bg-white/20 text-white' : 'bg-primary/10 text-primary'}
                          `}
                        >
                          {index + 1}
                        </span>
                        <span className="text-sm truncate max-w-24">{doc.title}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bg-muted/30 min-h-96 rounded-lg border p-2 overflow-auto">
                  {documents.map((doc) => (
                    <TabsContent key={doc.id} value={doc.id} className="mt-0 p-0 data-[state=active]:block">
                      <div className="flex justify-center">
                        {renderDocument(doc)}
                      </div>
                    </TabsContent>
                  ))}
                </div>
              </Tabs>
            </CardContent>
          </Card>

          <ResumeMatchAnalysisModal
            open={showAnalysisModal}
            onOpenChange={setShowAnalysisModal}
            analysisResults={analysisResults}
            changesAnalysis={changesAnalysis}
            beforeScore={beforeScore ?? undefined}
            afterScore={afterScore ?? undefined}
            jobTitle={jobTitle}
            companyName={companyName}
          />

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => navigate('/resume-tailoring-fixed-duplicate')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Tailoring
            </Button>
          </div>
        </div>
      </div>

      {/* Tailor Again Dialog */}
      <Dialog open={showTailorAgainDialog} onOpenChange={setShowTailorAgainDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-primary" />
              Tailor this Resume Again
            </DialogTitle>
            <DialogDescription>
              Add special instructions to refine your resume further. These instructions will be sent along with the current resume for re-tailoring.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="special-instructions" className="text-sm font-medium">
                Special Instructions
              </Label>
              <Textarea
                id="special-instructions"
                placeholder="Enter any specific changes or improvements you'd like to make...&#10;&#10;Examples:&#10;• Make the summary more focused on leadership&#10;• Add more quantifiable achievements&#10;• Emphasize cloud computing skills&#10;• Adjust tone to be more formal"
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
                rows={6}
                className="resize-none"
              />
            </div>
            
            <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">💡 Tip:</p>
              <p>Be specific about what changes you want. The more detail you provide, the better the results will be.</p>
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => setShowTailorAgainDialog(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitTailorAgain}
              disabled={isTailoringAgain || !specialInstructions.trim()}
              className="bg-gradient-to-r from-primary to-primary/80"
            >
              {isTailoringAgain ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Tailor Again
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

export default ResumeResults;
